import { useMutation, useQueryClient } from "@tanstack/react-query";

import { WithStringValues } from "common/utils/types";
import { useFetchQuery, useStateQuery } from "common/queries/hooks";

import {
  BrowseSearch,
  BrowseAllPatientKind,
  IProductLineDetails,
  IProductQueryResponse,
  ProductLineSearch,
  ProductLineListSearch,
  ProductSearchSearch,
  ProductSearch,
  IClientBrowse,
  IncentiveDetails,
  IncentiveItemsSearch,
  RebateDetails,
  RebateItemsSearch,
  FilterOptionsState,
  ProductLineSearchFilters,
  ProductSearchFilters,
  ClientCategory,
  PromotionItems,
  ProductLineAvailabilitySearch,
  ProductLineAvailabilityResponse,
  ItemPriceBreaksSearch,
  ItemPriceBreaks,
  ItemPricingSearch,
  ItemPricingResponse,
} from "../types";
import { keys, urls } from "./constants";

export const useProductLineQuery = (id: number) => {
  return useFetchQuery<IProductLineDetails>(keys.productLineDetail(id), {
    fetch: { url: urls.productLine(id) },
    config: { enabled: !!id },
  });
};

export const useProductQuery = (id: number, { enabled = true }: { enabled?: boolean } = {}) => {
  return useFetchQuery<IProductQueryResponse>(keys.productDetail(id), {
    fetch: { url: urls.product(id) },
    config: { enabled: enabled && !!id },
  });
};

/**
 * TODO sc-32581 Remove once we have a stable BE filter set returned on each search
 * or a separate endpoint for getting the filters
 * https://app.shortcut.com/vetcove/story/32581/
 * */
export const useCategoryProductLineFilterOptionState = (categoryIds: number[]) => {
  return useStateQuery<FilterOptionsState<ProductLineSearchFilters> | null>({
    queryKey: keys.categoryProductLineFilterOptionState(categoryIds),
    default: null,
  });
};

export const useProductLineSearchQuery = (
  search: ProductLineListSearch,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  const queryClient = useQueryClient();
  return useFetchQuery<ProductLineSearch, WithStringValues<ProductLineListSearch>>(
    keys.productLineSearch(search),
    {
      fetch: { url: urls.productLineSearch(), body: search },
      config: {
        enabled,
        keepPreviousData: true,
        onSuccess: (results) => {
          // we only want to update the filter options if the search query has changed
          // to have a stable set of filters for a given search query on the category page
          //
          // TODO sc-32581 Remove once we have a stable BE filter set returned on each search
          // or a separate endpoint for getting the filters
          // https://app.shortcut.com/vetcove/story/32581/
          const key = keys.categoryProductLineFilterOptionState(search.category_ids ?? []);
          const currentFilters = queryClient.getQueryData<
            FilterOptionsState<ProductLineSearchFilters> | undefined
          >(key);

          if (!currentFilters || currentFilters.q !== search.q) {
            queryClient.setQueryData<FilterOptionsState<ProductLineSearchFilters>>(key, {
              q: search.q,
              filters: results.filters,
            });
          }
        },
      },
    },
  );
};

/**
 * TODO sc-32581 Remove once we have a stable BE filter set returned on each search
 * or a separate endpoint for getting the filters
 * https://app.shortcut.com/vetcove/story/32581/
 * */
export const useProductFilterOptionState = () => {
  return useStateQuery<FilterOptionsState<ProductSearchFilters> | null>({
    queryKey: keys.productFilterOptionState(),
    default: null,
  });
};

export const useProductSearchQuery = (
  search: WithStringValues<ProductSearchSearch>,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  const queryClient = useQueryClient();
  return useFetchQuery<ProductSearch, WithStringValues<ProductSearchSearch>>(
    keys.productSearch(search),
    {
      fetch: { url: urls.productSearch(), body: search },
      config: {
        enabled,
        keepPreviousData: true,
        onSuccess: (results) => {
          // we only want to update the filter options if the search query has changed
          // to have a stable set of filters for a given search query
          //
          // TODO sc-32581 Remove once we have a stable BE filter set returned on each search
          // or a separate endpoint for getting the filters
          // https://app.shortcut.com/vetcove/story/32581/
          const currentFilters = queryClient.getQueryData<
            FilterOptionsState<ProductSearchFilters> | undefined
          >(keys.productFilterOptionState());

          if (!currentFilters || currentFilters.q !== search.q) {
            queryClient.setQueryData<FilterOptionsState<ProductSearchFilters>>(
              keys.productFilterOptionState(),
              {
                q: search.q,
                filters: results.filters,
              },
            );
          }
        },
      },
    },
  );
};

/**
 * TODO sc-32581 Remove once we have a stable BE filter set returned on each search
 * or a separate endpoint for getting the filters
 * https://app.shortcut.com/vetcove/story/32581/
 * */
export const useRebateProductFilterOptionState = () => {
  return useStateQuery<FilterOptionsState<ProductSearchFilters> | null>({
    queryKey: keys.rebateProductFilterOptionState(),
    default: null,
  });
};

export const useRebateItemsQuery = (id: number, search: RebateItemsSearch) => {
  const queryClient = useQueryClient();
  return useFetchQuery<PromotionItems, WithStringValues<RebateItemsSearch>>(
    keys.rebateItemList(id, search),
    {
      fetch: { url: urls.rebateItems(id), body: search },
      config: {
        keepPreviousData: true,
        onSuccess: (results) => {
          // we only want to update the filter options if the search query has changed
          // to have a stable set of filters for a given search query
          //
          // TODO sc-32581 Remove once we have a stable BE filter set returned on each search
          // or a separate endpoint for getting the filters
          // https://app.shortcut.com/vetcove/story/32581/
          const currentFilters = queryClient.getQueryData<
            FilterOptionsState<ProductSearchFilters> | undefined
          >(keys.rebateProductFilterOptionState());

          if (!currentFilters) {
            queryClient.setQueryData<FilterOptionsState<ProductSearchFilters>>(
              keys.rebateProductFilterOptionState(),
              { q: "", filters: results.filters },
            );
          }
        },
      },
    },
  );
};

/**
 * TODO sc-32581 Remove once we have a stable BE filter set returned on each search
 * or a separate endpoint for getting the filters
 * https://app.shortcut.com/vetcove/story/32581/
 * */
export const useIncentiveProductFilterOptionState = () => {
  return useStateQuery<FilterOptionsState<ProductSearchFilters> | null>({
    queryKey: keys.incentiveProductFilterOptionState(),
    default: null,
  });
};

export const useIncentiveItemsQuery = (
  id: number,
  search: IncentiveItemsSearch,
  { enabled = !!id }: { enabled?: boolean } = {},
) => {
  const queryClient = useQueryClient();
  return useFetchQuery<PromotionItems, WithStringValues<IncentiveItemsSearch>>(
    keys.incentiveItemList(id, search),
    {
      fetch: { url: urls.incentiveItems(id), body: search },
      config: {
        enabled,
        keepPreviousData: true,
        onSuccess: (results) => {
          // we only want to update the filter options if the search query has changed
          // to have a stable set of filters for a given search query
          //
          // TODO sc-32581 Remove once we have a stable BE filter set returned on each search
          // or a separate endpoint for getting the filters
          // https://app.shortcut.com/vetcove/story/32581/
          const currentFilters = queryClient.getQueryData<
            FilterOptionsState<ProductSearchFilters> | undefined
          >(keys.incentiveProductFilterOptionState());

          if (!currentFilters) {
            queryClient.setQueryData<FilterOptionsState<ProductSearchFilters>>(
              keys.incentiveProductFilterOptionState(),
              { q: "", filters: results.filters },
            );
          }
        },
      },
    },
  );
};

export const useDeleteFiltersMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(() => queryClient.invalidateQueries(keys.productFilterOptionState()));
};

export const useBrowseQuery = (
  search: WithStringValues<BrowseSearch> = { species: BrowseAllPatientKind.ALL },
) => {
  return useFetchQuery<IClientBrowse, WithStringValues<BrowseSearch>>(keys.browseFilter(search), {
    fetch: { url: urls.browse(), body: search },
    config: { keepPreviousData: true },
  });
};

export const useCategoryQuery = (id: number) => {
  return useFetchQuery<ClientCategory>(keys.categoryDetail(id), {
    fetch: { url: urls.category(id) },
  });
};

export const useIncentiveQuery = (id: number = 0) => {
  return useFetchQuery<IncentiveDetails>(keys.incentiveDetail(id), {
    fetch: { url: urls.incentive(id) },
    config: { enabled: !!id },
  });
};

export const useRebateQuery = (id: number) => {
  return useFetchQuery<RebateDetails>(keys.rebateDetail(id), {
    fetch: { url: urls.rebate(id) },
  });
};

export const useProductLineAvailabilityQuery = (
  id: number,
  search: WithStringValues<ProductLineAvailabilitySearch>,
) => {
  return useFetchQuery<
    ProductLineAvailabilityResponse,
    WithStringValues<ProductLineAvailabilitySearch>
  >(keys.productLineDetailAvailability(id, search), {
    fetch: { url: urls.productLineAvailability(id), body: search },
  });
};

/**
 *  Query to fetch price breaks for a list of item ids
 *  BE only accepts item ids & price breaks depend on the shipping address.
 *  In a world where we're fetching price breaks for a cart//order, we need to also pass
 *  the shipping address & have the BE factor that in when figuring out the fulfiller
 */
export const useItemPriceBreaks = (itemIds: number[]) => {
  return useFetchQuery<ItemPriceBreaks, WithStringValues<ItemPriceBreaksSearch>>(
    keys.itemPriceBreaks({ item_ids: itemIds.sort() }),
    {
      fetch: { url: urls.itemPriceBreaks(), body: { item_ids: itemIds } },
      config: {},
    },
  );
};

/**
 * Query to fetch accurate server-side pricing for add-to-cart button
 * Returns total price including discounts, fees, and shipping with MAP pricing enforcement
 */
export const useItemAddToCartPricing = (
  itemId: number,
  quantity: number,
  isSubscriptionPrice?: boolean,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  return useFetchQuery<ItemPricingResponse, WithStringValues<ItemPricingSearch>>(
    keys.itemPrices(itemId, { quantity, is_subscription_price: isSubscriptionPrice ?? false }),
    {
      fetch: {
        url: urls.itemAddToCartPricing(itemId),
        body: {
          quantity: quantity.toString(),
          is_subscription_price: isSubscriptionPrice?.toString() ?? "false",
        },
      },
      config: {
        enabled: enabled && !!itemId && quantity > 0,
      },
    },
  );
};
